import axios from 'axios';
import apiConfig from '../components/pages/apiConfig.ts';

// Create a type for the session handler function
type SessionHandler = () => void;

let sessionExpiryHandler: SessionHandler | null = null;
let unauthorizedHandler: SessionHandler | null = null;

// Debounce flags to prevent multiple simultaneous session handlers
let sessionExpiryInProgress = false;
let unauthorizedInProgress = false;

// Function to set session handlers (called from SessionProvider)
export const setSessionHandlers = (
  onSessionExpiry: SessionHandler,
  onUnauthorized: SessionHandler
) => {
  sessionExpiryHandler = onSessionExpiry;
  unauthorizedHandler = onUnauthorized;
};

// Debounced session expiry handler
const debouncedSessionExpiry = () => {
  if (sessionExpiryInProgress || !sessionExpiryHandler) return;
  
  sessionExpiryInProgress = true;
  
  // Clear any pending API calls that might generate toasts
  setTimeout(() => {
    sessionExpiryHandler();
  }, 50); // Very short delay to let current call stack finish
  
  // Reset flag after a longer delay
  setTimeout(() => {
    sessionExpiryInProgress = false;
  }, 5000);
};

// Debounced unauthorized handler  
const debouncedUnauthorized = () => {
  if (unauthorizedInProgress || !unauthorizedHandler) return;
  
  unauthorizedInProgress = true;
  
  // Clear any pending API calls that might generate toasts
  setTimeout(() => {
    unauthorizedHandler();
  }, 50); // Very short delay to let current call stack finish
  
  // Reset flag after a longer delay
  setTimeout(() => {
    unauthorizedInProgress = false;
  }, 5000);
};

// List of error messages that indicate session issues
const SESSION_EXPIRED_MESSAGES = [
  'token expired',
  'jwt expired',
  'session expired',
  'invalid token',
  'token is invalid',
  'token mismatch',
  'token mismatched',
  'unauthorized',
  'authentication failed',
  'please login again',
  'session timeout',
  'access denied',
  'token verification failed',
  'invalid authentication'
];

const CONCURRENT_LOGIN_MESSAGES = [
  'user already logged in',
  'concurrent login detected',
  'account logged in elsewhere',
  'multiple sessions not allowed',
  'session conflict'
];

// Function to check if error message indicates session expiry
const isSessionExpired = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  return SESSION_EXPIRED_MESSAGES.some(keyword => 
    lowerMessage.includes(keyword)
  );
};

// Function to check if error message indicates concurrent login
const isConcurrentLogin = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  return CONCURRENT_LOGIN_MESSAGES.some(keyword => 
    lowerMessage.includes(keyword)
  );
};

// Create custom axios instance for session management
const sessionAxios = axios.create({
  baseURL: apiConfig.apiBaseURL,
  timeout: 10000,
});

// Request interceptor for session axios
sessionAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Common response interceptor logic
const createResponseInterceptor = (axiosInstance: any) => {
  return axiosInstance.interceptors.response.use(
    (response: any) => {
      // Check for session issues even in successful responses
      if (response.data) {
        const { status, message } = response.data;
        
        if (status === 'Failed' || status === 'Error') {
          const errorMessage = message || '';
          
          if (isSessionExpired(errorMessage)) {
            debouncedSessionExpiry();
            return Promise.reject(new Error('Session expired'));
          }
          
          if (isConcurrentLogin(errorMessage)) {
            debouncedUnauthorized();
            return Promise.reject(new Error('Concurrent login detected'));
          }
        }
      }
      
      return response;
    },
    (error: any) => {
      // Handle HTTP error responses
      if (error.response) {
        const { status, data } = error.response;
        
        // Handle 401 Unauthorized
        if (status === 401) {
          debouncedUnauthorized();
          return Promise.reject(error);
        }
        
        // Handle 403 Forbidden
        if (status === 403) {
          debouncedSessionExpiry();
          return Promise.reject(error);
        }
        
        // Check error message in response data
        if (data && data.message) {
          const errorMessage = data.message;
          
          if (isSessionExpired(errorMessage)) {
            debouncedSessionExpiry();
            return Promise.reject(error);
          }
          
          if (isConcurrentLogin(errorMessage)) {
            debouncedUnauthorized();
            return Promise.reject(error);
          }
        }
      }
      
      // Handle network errors or other issues
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        // Don't logout on network errors, just propagate the error
        return Promise.reject(error);
      }
      
      return Promise.reject(error);
    }
  );
};

// Set up interceptors for both default axios and session axios
createResponseInterceptor(axios);
createResponseInterceptor(sessionAxios);

// Also apply to any existing axios instances that might be imported from other modules
setTimeout(() => {
  // Apply interceptor to any axios instances created elsewhere
  const axiosInstances = [axios, sessionAxios];
  
  // Try to find other axios instances
  if (typeof window !== 'undefined' && (window as any).axiosInstance) {
    axiosInstances.push((window as any).axiosInstance);
  }
  
  axiosInstances.forEach(instance => {
    if (instance && instance.interceptors && instance.interceptors.response) {
      createResponseInterceptor(instance);
    }
  });
}, 100);

// Export the session axios for use in other parts of the app
export { sessionAxios };

export default axios;